import { EmbeddedMessage } from 'constants/messageMediums';
import {
  Campaign,
  CampaignStateDefinitions,
  CampaignTypeDefinitions,
} from 'modules/campaignIndex/domainTypes';
import {
  Archive16,
  Calendar16,
  Confirm16,
  Edit16,
  EmailLine16,
  InAppLine16,
  Play16,
  PushLine16,
  Refresh16,
  Send16,
  SmsLine16,
  Undo16,
  WebPushLine16,
} from 'ui/icons';
import { createObjectWithKey } from 'utils/helpers';

type CampaignRecordObject = {
  text: string;
  value: string;
  icon?: (props: any) => JSX.Element;
};

/** Default params for fetching campaigns. */
export const defaultCampaignParams = {
  page: 1,
  pageSize: 10,
  orderBy: 'id',
  sorting: 'desc',
};

/**
 * **getCampaignId**_(campaign)_<br>
 * Get the id of a campaign
 * @param { Object } campaign: The campaign data object.
 * @returns { String } The campaign id.
 */
export const getCampaignId = (campaign: Campaign) => campaign?.id;

/**
 * **getCampaignName**_(campaign)_<br>
 * Get the name of a campaign
 * @param { Object } campaign: The campaign data object.
 * @returns { String } The campaign name.
 */
export const getCampaignName = (campaign: Campaign) => campaign?.name;

/**
 * **getTemplateId**_(campaign)_<br>
 * Get the template id of a campaign
 * @param { Object } campaign: The campaign data object.
 * @returns { String } The campaign template id.
 */
export const getTemplateId = (campaign: Campaign) => campaign?.templateId;

export const campaignMediums: Record<string, CampaignRecordObject> = {
  ALL: { text: 'All Mediums', value: 'All' },
  EMAIL: { text: 'Email', value: 'Email', icon: EmailLine16 },
  PUSH: { text: 'Push', value: 'Push', icon: PushLine16 },
  SMS: { text: 'SMS', value: 'SMS', icon: SmsLine16 },
  IN_APP: { text: 'In-App', value: 'InApp', icon: InAppLine16 },
  WEB_PUSH: { text: 'Web Push', value: 'WebPush', icon: WebPushLine16 },
};

export const campaignMediumTextFromValue = Object.keys(campaignMediums)
  .map((key) => createObjectWithKey(campaignMediums[key].value, campaignMediums[key].text))
  .reduce((obj, unit) => {
    let newObj = obj;
    newObj = { ...newObj, ...unit };
    return newObj;
  });

/*------------------------------------------------------------------------------------------------*/
/* Campaign States Utils                                                                          */
/*------------------------------------------------------------------------------------------------*/
export const campaignStates: Record<string, CampaignRecordObject> = {
  ALL: { text: 'All States', value: 'All' },
  DRAFT: { text: 'Draft', value: CampaignStateDefinitions.Draft, icon: Edit16 },
  FINISHED: { text: 'Finished', value: CampaignStateDefinitions.Finished, icon: Confirm16 },
  READY: { text: 'Ready', value: CampaignStateDefinitions.Ready, icon: Play16 },
  RECURRING: { text: 'Recurring', value: CampaignStateDefinitions.Recurring, icon: Refresh16 },
  RUNNING: { text: 'Running', value: CampaignStateDefinitions.Running, icon: Send16 },
  RECALLING: { text: 'Recalling', value: CampaignStateDefinitions.Recalling, icon: Undo16 },
  RECALLED: { text: 'Recalled', value: CampaignStateDefinitions.Recalled, icon: Archive16 },
  SCHEDULED: {
    text: 'Scheduled',
    value: CampaignStateDefinitions.Scheduled,
    icon: Calendar16,
  },
};

export const campaignStateTextFromValue = Object.keys(campaignStates)
  .map((key) => createObjectWithKey(campaignStates[key].value, campaignStates[key].text))
  .reduce((obj, unit) => {
    let newObj = obj;
    newObj = { ...newObj, ...unit };
    return newObj;
  });

/**
 * **getCampaignState**_(campaign)_<br>
 * Get the state of a campaign
 * @param { Object } campaign: The campaign data object.
 * @returns { String } The campaign state.
 */
export const getCampaignState = (campaign: Campaign) => campaign?.campaignState;

/**
 * **isAborted**_(campaign)_<br>
 * Determines if the state of the campaign is 'Aborted'
 * @param { Object } campaign: The campaign data object.
 * @returns { Boolean } If the state is 'Aborted'.
 */
export const isAborted = (campaign: Campaign) =>
  getCampaignState(campaign) === CampaignStateDefinitions.Aborted;

/**
 * **isArchived**_(campaign)_<br>
 * Determines if the state of the campaign is 'Archived'
 * @param { Object } campaign: The campaign data object.
 * @returns { Boolean } If the state is 'Archived'.
 */
export const isArchived = (campaign: Campaign) =>
  getCampaignState(campaign) === CampaignStateDefinitions.Archived;

/**
 * **isDraft**_(campaign)_<br>
 * Determines if the state of the campaign is 'Draft'
 * @param { Object } campaign: The campaign data object.
 * @returns { Boolean } If the state is 'draft'.
 */
export const isDraft = (campaign: Campaign) =>
  getCampaignState(campaign) === campaignStates.DRAFT.value;

/**
 * **isFinished**_(campaign)_<br>
 * Determines if the state of the campaign is 'Finished'
 * @param { Object } campaign: The campaign data object.
 * @returns { Boolean } If the state is 'Finished'.
 */
export const isFinished = (campaign: Campaign) =>
  getCampaignState(campaign) === campaignStates.FINISHED.value;

/**
 * **isReady**_(campaign)_<br>
 * Determines if the state of the campaign is 'Ready'
 * @param { Object } campaign: The campaign data object.
 * @returns { Boolean } If the state is 'Ready'.
 */
export const isReady = (campaign: Campaign) =>
  getCampaignState(campaign) === campaignStates.READY.value;

/**
 * **isRecurring**_(campaign)_<br>
 * Determines if the state of the campaign is 'Recurring'
 * @param { Object } campaign: The campaign data object.
 * @returns { Boolean } If the state is 'Recurring'.
 */
export const isRecurring = (campaign: Campaign) =>
  getCampaignState(campaign) === campaignStates.RECURRING.value;

/**
 * **isRunning**_(campaign)_<br>
 * Determines if the state of the campaign is 'Running'
 * @param { Object } campaign: The campaign data object.
 * @returns { Boolean } If the state is 'Running'.
 */
export const isRunning = (campaign: Campaign) =>
  getCampaignState(campaign) === campaignStates.RUNNING.value;

/**
 * **isScheduled**_(campaign)_<br>
 * Determines if the state of the campaign is 'Scheduled'
 * @param { Object } campaign: The campaign data object.
 * @returns { Boolean } If the state is 'Scheduled'.
 */
export const isScheduled = (campaign: Campaign) =>
  getCampaignState(campaign) === campaignStates.SCHEDULED.value;

/**
 * **wasRecurring**_(campaign)_<br>
 * Determines if the the campaign was ever a recurring campaign
 * @param { Object } campaign: The campaign data object.
 * @returns { Boolean } If the campaign was recurring.
 */
export const wasRecurring = (campaign: Campaign) =>
  // @ts-ignore
  getCampaignState(campaign) === campaignStates.RECURRING.value || campaign?.wasEverRecurring;

/*------------------------------------------------------------------------------------------------*/
/* Campaign URL                                                                                   */
/*------------------------------------------------------------------------------------------------*/

export const getCampaignUrl = (campaign: Campaign, campaignReactificationV1?: boolean) => {
  const campaignId = getCampaignId(campaign);

  // check if the feature flag is on for the reactification of campaigns, if not follow the old path.
  // we intentially did not modify the previous way of generating the url to avoid any regression.

  if (campaignReactificationV1) {
    if (isDraft(campaign)) {
      return `/campaigns/${campaignId}`;
    }
    if (isReady(campaign) || isScheduled(campaign)) {
      return `/campaigns/${campaignId}?view=Summary`;
    }
    if (wasRecurring(campaign)) {
      return `/campaigns/recurring/${campaignId}`;
    }
    return `/analytics/campaignPerformance?campaignId=${campaignId}`;
  } else {
    if (isDraft(campaign) || isReady(campaign)) {
      return `/templates/edit?templateId=${getTemplateId(campaign)}&campaignId=${campaignId}`;
    }
    if (isScheduled(campaign)) {
      return `/campaigns/review?campaignId=${campaignId}`;
    }
    if (wasRecurring(campaign)) {
      return `/campaigns/recurring/${campaignId}`;
    }
    return `/analytics/campaignPerformance?campaignId=${campaignId}`;
  }
};

/*------------------------------------------------------------------------------------------------*/
/* Misc Campaign Utils                                                                            */
/*------------------------------------------------------------------------------------------------*/

export const isTriggeredByApi = (campaign: Campaign) =>
  campaign.campaignTypeDefinition === CampaignTypeDefinitions.ApiTriggered;

export const isTriggeredByWorkflow = (campaign: Campaign) =>
  campaign.campaignTypeDefinition === CampaignTypeDefinitions.WorkflowTriggered;

export const isTriggeredByEligibility = (campaign: Campaign) =>
  campaign.campaignTypeDefinition === CampaignTypeDefinitions.Eligibility ||
  (campaign.campaignTypeDefinition === CampaignTypeDefinitions.WorkflowTriggered &&
    campaign.messageMedium === EmbeddedMessage);
